import type { CardId, DrawnCard, Spread, Tier, UserPrefs } from './types.js';
import { REVERSAL_ODDS } from './types.js';
import { entitlementsFor, lockedPositions } from './entitlements.js';

/** Injectable so the server can use CSPRNG and tests can be deterministic. */
export type Rng = () => number;

export const defaultRng: Rng = () => Math.random();

/** Fisher-Yates over a copy; draws the first `count` cards. */
export function drawCardIds(pool: CardId[], count: number, rng: Rng = defaultRng): CardId[] {
  if (count > pool.length) {
    throw new Error(`Cannot draw ${count} cards from a pool of ${pool.length}`);
  }

  const deck = [...pool];
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = deck[i]!;
    const b = deck[j]!;
    deck[i] = b;
    deck[j] = a;
  }

  return deck.slice(0, count);
}

/**
 * Which positions come up reversed for a FREE reading.
 *
 * The SPEC asks for reversals to alternate deterministically rather than roll
 * randomly: reading 1 has one, reading 2 has none, reading 3 has one, and so
 * on. `readingNumber` is 1-based and persisted on the device, so the pattern
 * survives reloads.
 *
 * The reversed card lands in the first or second position, itself alternating,
 * so free users don't learn that it is always card one.
 */
export function freeReversedPositions(readingNumber: number, spread: Spread): number[] {
  const isReversalReading = readingNumber % 2 === 1;
  if (!isReversalReading) return [];

  if (spread === 1) return [0];

  // Readings 1, 5, 9… reverse the first card; 3, 7, 11… the second.
  const slot = Math.floor((readingNumber - 1) / 2) % 2;
  return [slot];
}

/** Independent per-card roll for paid readings, using the user's frequency pref. */
export function paidReversedPositions(
  spread: Spread,
  prefs: Pick<UserPrefs, 'reversalsEnabled' | 'reversalFrequency'>,
  rng: Rng = defaultRng,
): number[] {
  if (!prefs.reversalsEnabled) return [];

  const odds = REVERSAL_ODDS[prefs.reversalFrequency];
  const positions: number[] = [];
  for (let i = 0; i < spread; i++) {
    if (rng() < odds) positions.push(i);
  }
  return positions;
}

export interface BuildSpreadOptions {
  tier: Tier;
  spread: Spread;
  pool: CardId[];
  prefs: Pick<UserPrefs, 'reversalsEnabled' | 'reversalFrequency'>;
  /** 1-based count of free readings already taken, +1. Ignored for paid tiers. */
  readingNumber: number;
  rng?: Rng;
}

/**
 * The single place a spread is assembled, shared by the offline client (free
 * draws) and the server (paid draws). Keeping it here means the free and paid
 * paths cannot drift apart in how reversals or locks are applied.
 */
export function buildSpread(options: BuildSpreadOptions): DrawnCard[] {
  const { tier, spread, pool, prefs, readingNumber, rng = defaultRng } = options;
  const ents = entitlementsFor(tier);

  if (!ents.spreads.includes(spread)) {
    throw new Error(`Tier "${tier}" may not draw a ${spread}-card spread`);
  }

  const ids = drawCardIds(pool, spread, rng);

  // Free users always have reversals on and always get the fixed pattern;
  // only paid users may switch them off or change their frequency.
  const reversed = new Set(
    ents.configurableReversals
      ? paidReversedPositions(spread, prefs, rng)
      : freeReversedPositions(readingNumber, spread),
  );
  const locked = new Set(lockedPositions(tier, spread));

  return ids.map((cardId, position) => ({
    cardId,
    position,
    reversed: reversed.has(position),
    ...(locked.has(position) ? { locked: true } : {}),
  }));
}
